<template>
  <div class="legal-doc">
    <APageHead
      title="약관 문서 관리"
      sub="이용약관·개인정보처리방침 등 법적 문서의 본문을 버전 관리하며 발행합니다."
    >
      <template #actions>
        <ABtn kind="ghost" icon="fact_check" @click="goConsents">동의 증빙 조회</ABtn>
        <ABtn kind="primary" icon="add" @click="openCreate">문서 등록</ABtn>
      </template>
    </APageHead>

    <!--
      노출 ON 인데 발행본이 없으면 서버가 그 문서를 건너뛰고 가입을 진행시킵니다(fail-open).
      관리자에겐 "노출 중"으로 보이는데 동의는 한 건도 안 쌓이는 상태라 최상단에 크게 띄웁니다.
    -->
    <div v-if="!loading && brokenDocs.length" class="legal-doc__banner legal-doc__banner--err">
      <i class="material-icons-round">gpp_maybe</i>
      <div>
        <b>지금 앱에서 누락되고 있는 문서가 {{ brokenDocs.length }}건 있습니다.</b>
        <span>
          <b>{{ brokenNames }}</b> — 노출은 켜져 있지만 발행된 본문이 없어,
          서버가 이 문서를 건너뛰고 가입을 진행시킵니다.
          <b>동의가 수집되지 않고 있으니</b> 본문을 즉시 발행하세요.
        </span>
      </div>
    </div>

    <!-- 발행 → 활성화 순서를 놓치면 앱에 아무것도 안 보입니다. 준비 중인 문서를 요약합니다. -->
    <div v-if="!loading && inactiveCount > 0" class="legal-doc__banner">
      <i class="material-icons-round">report_problem</i>
      <div>
        <b>앱에 노출되지 않는 문서가 {{ inactiveCount }}건 있습니다.</b>
        <span><b>본문 발행 → 노출 켜기</b> 순서로 진행하세요.</span>
      </div>
    </div>

    <!-- KPI -->
    <div class="legal-doc__stats">
      <ACard><AStat label="전체 문서" :value="documents.length" unit="건" /></ACard>
      <ACard><AStat label="앱에 서빙 중" :value="servingCount" unit="건" /></ACard>
      <ACard><AStat label="본문 미등록" :value="noBodyCount" unit="건" /></ACard>
      <ACard><AStat label="작성 중 초안" :value="draftTotal" unit="건" /></ACard>
    </div>

    <ACard>
      <template #title>
        <span class="legal-doc__sec-title">
          <i class="material-icons-round legal-doc__sec-icon">gavel</i>
          문서 목록
        </span>
      </template>
      <template #action>
        <APill kind="info">{{ documents.length }}건</APill>
      </template>

      <div v-if="loading" class="legal-doc__loading">
        <div class="p-spinner p-spinner--lg"></div>
        <p>문서 목록을 불러오는 중...</p>
      </div>

      <AEmpty
        v-else-if="!documents.length"
        title="등록된 약관 문서가 없습니다"
        sub="'문서 등록'으로 이용약관·개인정보처리방침 등을 먼저 만드세요."
      />

      <ATable v-else :columns="columns" :rows="documents" row-key="idx" striped bordered>
        <template #cell:name="{ row }">
          <button type="button" class="legal-doc__name" @click="goDetail(row)">
            <span class="legal-doc__name-ko">{{ row.names && row.names.ko || row.documentId }}</span>
            <span class="legal-doc__name-id">{{ row.documentId }}</span>
          </button>
        </template>

        <template #cell:docType="{ row }">
          <APill kind="info">{{ docTypeLabel(row.docType) }}</APill>
        </template>

        <template #cell:isRequired="{ row }">
          <APill :kind="row.isRequired ? 'dark' : 'default'">{{ row.isRequired ? '필수' : '선택' }}</APill>
        </template>

        <template #cell:targetUserType="{ row }">
          <span class="legal-doc__target">{{ targetUserTypeLabel(row.targetUserType) }}</span>
        </template>

        <template #cell:currentVersion="{ row }">
          <div v-if="row.currentVersion" class="legal-doc__version">
            <span class="legal-doc__mono">v{{ row.currentVersion }}</span>
            <span class="legal-doc__version-date">시행 {{ fmtKst(row.currentEffectiveAt) }}</span>
          </div>
          <APill v-else kind="err" dot>본문 등록 필요</APill>
        </template>

        <template #cell:draftCount="{ row }">
          <APill v-if="row.draftCount > 0" kind="yellow">작성 중 {{ row.draftCount }}건</APill>
          <span v-else class="legal-doc__dash">—</span>
        </template>

        <template #cell:activeYn="{ row }">
          <div class="legal-doc__active">
            <button
              type="button"
              :class="['p-switch', { 'is-on': row.activeYn === 'Y' }]"
              :title="exposure(row).hint"
              @click="toggleActive(row)"
            ></button>
            <APill :kind="exposure(row).kind" dot>{{ exposure(row).label }}</APill>
          </div>
        </template>

        <template #cell:_actions="{ row }">
          <div class="legal-doc__actions">
            <ABtn size="sm" kind="primary" icon="history_edu" @click="goDetail(row)">버전 관리</ABtn>
            <ABtn size="sm" kind="ghost" icon="edit" icon-only title="문서 정보 수정" @click="openEdit(row)" />
          </div>
        </template>
      </ATable>
    </ACard>

    <LegalDocumentFormModal
      v-model="showForm"
      :doc="editingDoc"
      :documents="documents"
      @saved="onSaved"
    />
  </div>
</template>

<script>
import swal from "sweetalert2";
import APageHead from "@/components/Pineapple/APageHead.vue";
import ACard from "@/components/Pineapple/ACard.vue";
import AStat from "@/components/Pineapple/AStat.vue";
import ABtn from "@/components/Pineapple/ABtn.vue";
import APill from "@/components/Pineapple/APill.vue";
import ATable from "@/components/Pineapple/ATable.vue";
import AEmpty from "@/components/Pineapple/AEmpty.vue";
import LegalDocumentFormModal from "./components/LegalDocumentFormModal.vue";
import { apiErrorMessage, apiErrorStatus } from "@/utils/apiError";
import { fmtKst, docTypeLabel, targetUserTypeLabel } from "@/utils/legal";

export default {
  name: "SystemLegalDocuments",
  components: {
    APageHead,
    ACard,
    AStat,
    ABtn,
    APill,
    ATable,
    AEmpty,
    LegalDocumentFormModal,
  },
  data() {
    return {
      loading: true,
      documents: [],
      showForm: false,
      editingDoc: null,
      columns: [
        { key: "name", label: "문서", width: 230 },
        { key: "docType", label: "유형", width: 130, align: "center" },
        { key: "isRequired", label: "필수", width: 80, align: "center" },
        { key: "targetUserType", label: "대상", width: 80, align: "center" },
        { key: "currentVersion", label: "현행 버전", width: 165 },
        { key: "draftCount", label: "초안", width: 110, align: "center" },
        { key: "activeYn", label: "앱 노출", width: 150, align: "center" },
        { key: "_actions", label: "관리", align: "right", width: 180 },
      ],
    };
  },
  computed: {
    // 실제로 앱에 서빙되는 문서 = 노출 ON 이면서 발행본이 있는 것
    servingCount() {
      return this.documents.filter((d) => d.activeYn === "Y" && d.currentVersion).length;
    },
    inactiveCount() {
      return this.documents.filter((d) => d.activeYn !== "Y").length;
    },
    noBodyCount() {
      return this.documents.filter((d) => !d.currentVersion).length;
    },
    draftTotal() {
      return this.documents.reduce((sum, d) => sum + (d.draftCount || 0), 0);
    },
    // 노출은 켜져 있는데 발행본이 없어, 앱에서 조용히 누락되는 문서
    brokenDocs() {
      return this.documents.filter((d) => d.activeYn === "Y" && !d.currentVersion);
    },
    brokenNames() {
      return this.brokenDocs.map((d) => (d.names && d.names.ko) || d.documentId).join(", ");
    },
  },
  created() {
    this.fetchDocuments();
  },
  methods: {
    fmtKst,
    docTypeLabel,
    targetUserTypeLabel,
    /**
     * 실제 앱 노출 상태는 activeYn 단독이 아니라 currentVersion 과의 조합으로 결정됩니다.
     * 노출 ON + 발행본 없음 이면 서버가 fail-open 으로 그 문서를 건너뛰어, 관리자는
     * "노출 중"으로 보이는데 앱에서는 동의가 조용히 수집되지 않습니다. 가장 위험한
     * 상태라 초록이 아니라 빨강으로 표시합니다.
     */
    exposure(row) {
      if (row.activeYn !== "Y") {
        return { kind: "warn", label: "미노출", hint: "미노출 — 켜기" };
      }
      if (!row.currentVersion) {
        return {
          kind: "err",
          label: "노출 ON · 본문 없음",
          hint: "발행본이 없어 앱에서 누락됩니다 — 본문을 먼저 발행하세요",
        };
      }
      return { kind: "ok", label: "노출 중", hint: "앱 노출 중 — 끄기" };
    },
    goDetail(row) {
      this.$router.push({
        name: "SystemLegalDocumentDetail",
        params: { documentId: row.documentId },
      });
    },
    goConsents() {
      this.$router.push({ name: "SystemLegalConsents" });
    },
    openCreate() {
      this.editingDoc = null;
      this.showForm = true;
    },
    openEdit(row) {
      this.editingDoc = row;
      this.showForm = true;
    },
    onSaved() {
      this.fetchDocuments();
    },
    async fetchDocuments() {
      this.loading = true;
      try {
        const res = await this.$axios({
          method: "get",
          url: "/emp/legal/documents",
          loadingBar: true,
        });
        this.documents = [...(res.data || [])].sort(
          (a, b) => (a.sortOrder || 0) - (b.sortOrder || 0)
        );
      } catch (e) {
        console.error("약관 문서 목록 조회 실패:", e);
        swal.fire("오류", apiErrorMessage(e, "문서 목록을 불러오지 못했습니다."), "error");
      } finally {
        this.loading = false;
      }
    },
    /**
     * 활성 토글 = 앱 노출 on/off. 발행과는 별개 플래그입니다.
     * 발행본이 없는 문서를 켜려 하면 서버가 409로 막습니다.
     */
    async toggleActive(row) {
      const next = row.activeYn === "Y" ? "N" : "Y";
      const name = (row.names && row.names.ko) || row.documentId;

      const ok = await swal.fire({
        icon: "warning",
        title: next === "Y" ? "앱 노출을 시작합니다" : "앱 노출을 중단합니다",
        text:
          next === "Y"
            ? `'${name}' 이 앱 가입 화면에 노출되고 동의 수집이 시작됩니다.`
            : `'${name}' 이 앱에서 숨겨지고 동의 수집이 중단됩니다.`,
        showCancelButton: true,
        confirmButtonText: next === "Y" ? "노출 시작" : "노출 중단",
        cancelButtonText: "취소",
      });
      if (!ok.isConfirmed) return;

      try {
        await this.$axios({
          method: "patch",
          url: `/emp/legal/documents/${encodeURIComponent(row.documentId)}/active`,
          data: { activeYn: next },
          loadingBar: true,
        });
        this.$notifyVue({
          type: "success",
          title: next === "Y" ? `'${name}' 을 앱에 노출합니다.` : `'${name}' 노출을 중단했습니다.`,
        });
        this.fetchDocuments();
      } catch (e) {
        console.error("활성 토글 실패:", e);
        const message = apiErrorMessage(e, "노출 상태를 바꾸지 못했습니다.");

        // 409 = 발행본이 없는 문서. 다음에 할 일(본문 발행)로 바로 보내 줍니다.
        if (apiErrorStatus(e) === 409) {
          const go = await swal.fire({
            icon: "warning",
            title: "활성화할 수 없습니다",
            text: message,
            showCancelButton: true,
            confirmButtonText: "본문 발행하러 가기",
            cancelButtonText: "닫기",
          });
          if (go.isConfirmed) {
            this.goDetail(row);
            return;
          }
        } else {
          await swal.fire("변경 실패", message, "error");
        }
        this.fetchDocuments();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/pineapple";

.legal-doc {
  display: flex;
  flex-direction: column;
  gap: 20px;

  &__banner {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 13px 16px;
    background: $p-warn-soft;
    border: 1px solid rgba($p-warn, 0.35);
    border-radius: $p-radius-md;

    > i {
      font-size: 20px !important;
      color: $p-warn-ink;
    }

    > div {
      display: flex;
      flex-direction: column;
      gap: 2px;

      b { font-size: 12px; font-weight: 800; color: $p-ink; }
      span { font-size: 11px; line-height: 1.5; color: $p-ink-mid; }
    }

    // 지금 이 순간 동의가 수집되지 않고 있는 상태 — 준비 중(노랑)과 구분합니다.
    &--err {
      background: $p-err-soft;
      border-color: rgba($p-err, 0.35);

      > i { color: $p-err-ink; }
      > div > b { color: $p-err-ink; }
    }
  }

  &__stats {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 16px;

    @media (max-width: 1100px) { grid-template-columns: repeat(2, 1fr); }
    @media (max-width: 640px) { grid-template-columns: 1fr; }
  }

  &__sec-title {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    font-weight: 800;
    color: $p-ink;
  }

  &__sec-icon {
    color: $p-point-deep;
    font-size: 16px !important;
  }

  &__loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    padding: 48px 24px;
    color: $p-ink-mid;

    p { margin: 0; font-size: 12px; }
  }

  &__name {
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: 0;
    border: 0;
    background: transparent;
    text-align: left;
    cursor: pointer;
    font-family: $p-font-body;

    &:hover .legal-doc__name-ko { color: $p-point-deep; }
  }

  &__name-ko {
    font-size: 13px;
    font-weight: 800;
    color: $p-ink;
    transition: color $p-dur $p-ease;
  }

  &__name-id {
    font-family: $p-font-mono;
    font-size: 10px;
    color: $p-ink-soft;
  }

  &__target {
    font-size: 12px;
    color: $p-ink-mid;
  }

  &__version {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  &__version-date {
    font-size: 10px;
    color: $p-ink-soft;
  }

  &__mono {
    font-family: $p-font-mono;
    font-size: 12px;
    font-weight: 700;
    color: $p-ink;
  }

  &__dash {
    color: $p-ink-softer;
  }

  &__active {
    display: inline-flex;
    align-items: center;
    gap: 8px;
  }

  &__actions {
    display: inline-flex;
    align-items: center;
    gap: 5px;
  }
}
</style>
